import { useState } from "react";
import { Square, Box, RefreshCw, ArrowUpRight } from "lucide-react";
import QuickRequestModal from "@/components/forms/QuickRequestModal";
import { useAuth } from "@/context/AuthContext";

const services = [
  {
    key: "2d",
    planType: "2D Floor Plan",
    icon: Square,
    title: "2D Floor Plans",
    desc: "MLS-ready black & white or color floor plans with precise dimensions, room labels and total square footage — drafted to ANSI/REBAC standards.",
    testId: "service-card-2d",
    accent: "from $129",
    tint: "bg-teal-50",
    iconTint: "bg-teal-100 text-teal-700",
    chipTint: "bg-teal-100 text-teal-800",
  },
  {
    key: "3d",
    planType: "3D Floor Plan",
    icon: Box,
    title: "3D Floor Plans",
    desc: "Photo-realistic 3D renderings with furniture, textures and finishes. Help buyers feel the space before they walk through the door.",
    testId: "service-card-3d",
    accent: "from $179",
    tint: "bg-amber-50",
    iconTint: "bg-amber-100 text-amber-700",
    chipTint: "bg-amber-100 text-amber-800",
  },
  {
    key: "renew",
    planType: "Renew Existing Floor Plan",
    icon: RefreshCw,
    title: "Renew Old Plans",
    desc: "Send us your existing floor plan and we'll refresh it — clean linework, updated dimensions, new branding, and modern file formats.",
    testId: "service-card-renew",
    accent: "from $69",
    tint: "bg-rose-50",
    iconTint: "bg-rose-100 text-rose-700",
    chipTint: "bg-rose-100 text-rose-800",
  },
];

export default function Services() {
  const { user, authHeader } = useAuth();
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section
      id="services"
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF7F2]"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
          <div className="lg:col-span-7">
            <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
              01 — What we draw
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-[1.05]">
              Three services.<br />
              <span className="italic font-medium text-teal-700">One trusted local team.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-stone-600 text-lg leading-relaxed">
              Every plan is measured on-site by our local technicians,
              hand-checked, and delivered in editable formats (PDF, PNG, DXF).
              No outsourcing. No guessing.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, idx) => {
            const { key: _key, ...rest } = s;
            return (
              <ServiceCard
                key={s.key}
                index={idx}
                {...rest}
                onClick={() => setSelectedService(s)}
              />
            );
          })}
        </div>
      </div>

      <QuickRequestModal
        open={!!selectedService}
        onClose={() => setSelectedService(null)}
        prefill={{
          name: user?.name,
          email: user?.email,
          planType: selectedService?.planType,
        }}
        authHeader={authHeader}
      />
    </section>
  );
}

function ServiceCard({
  icon: Icon,
  title,
  desc,
  testId,
  accent,
  index,
  tint,
  iconTint,
  chipTint,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative text-left ${tint} border border-stone-200 rounded-[2rem] p-8 lg:p-9 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 focus:ring-offset-[#FAF7F2]`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-8">
        <div className={`h-14 w-14 rounded-2xl ${iconTint} flex items-center justify-center`}>
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        </div>
        <div className="h-10 w-10 rounded-full bg-white border border-stone-200 flex items-center justify-center group-hover:bg-stone-900 group-hover:text-white transition-colors">
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-12" />
        </div>
      </div>

      <span className={`inline-block ${chipTint} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4`}>
        0{index + 1} · {accent}
      </span>
      <h3 className="font-display text-2xl lg:text-3xl font-semibold text-stone-900 leading-tight">
        {title}
      </h3>
      <p className="mt-4 text-stone-600 text-base leading-relaxed">
        {desc}
      </p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-stone-700 group-hover:text-teal-700 transition-colors">
        Request this →
      </span>
    </button>
  );
}
